import { FilterValue, FilterValueType } from '../types';

/**
 * Filter Operator for validating the field value is in the collection of filter values
 */
export function inCollection<T>(row: T, fields: (keyof T)[], _: FilterValueType, filterValues: FilterValue, fieldProperty?: string) {
	// A collection operator with no collection matches nothing. Saved queries are server data, and this runs inside the
	// board's row selector, so throwing on one would take the whole grid down rather than drop the one filter.
	if (filterValues === undefined || filterValues === null) {
		return false;
	}

	if (typeof filterValues !== 'object' || (!isStringArray(filterValues) && !isNumberArray(filterValues))) {
		throw new Error(`In Collection operator received invalid comparison value of ${typeof filterValues}`);
	}

	const isInCollection: boolean = fields.reduce((fieldIsInCollection, field) => {
		if (filterValues.length === 0) {
			return false;
		}

		// Since we are always using OR logic, once we find a single match the rest doesn't matter just continue
		if (fieldIsInCollection) {
			return true;
		}

		let fieldValue: unknown = row[field];
		if (fieldValue && typeof fieldValue === 'object' && fieldProperty) {
			const obj = fieldValue as Record<string, unknown>;
			fieldValue = obj[fieldProperty];
		}

		// Absence disqualifies a field, not falsiness: 0 is a real value to match on, and Leg Priority stores one
		if (fieldValue === null || fieldValue === undefined || fieldValue === '' || typeof fieldValue === 'boolean') {
			return false;
		}

		switch (typeof fieldValue) {
			case 'string':
				if (!isStringArray(filterValues)) {
					throw new Error('In Collection operator cannot compare field type "string" with Filter Value type "number".');
				}

				// includes is case-sensitive so using findIndex instead
				return (
					filterValues.findIndex(
						(str) =>
							str.localeCompare(fieldValue, 'en', {
								sensitivity: 'base'
							}) === 0
					) !== -1
				);

			case 'number':
				if (!isNumberArray(filterValues)) {
					throw new Error('In Collection operator cannot compare field type "number" with Filter Value type "string".');
				}
				return filterValues.includes(fieldValue);

			default:
				throw new Error(`In Collection operator cannot be applied to ${typeof fieldValue}`);
		}
	}, false);

	return isInCollection;
}

/**
 * Filter Operator for validating the field value is not in the collection of filter values
 */
export function notInCollection<T>(row: T, fields: (keyof T)[], _: FilterValueType, filterValues: FilterValue) {
	return !inCollection(row, fields, _, filterValues);
}

/**
 * Type Guard ensuring the array is strings
 */
function isStringArray(array: string[] | number[] | boolean[] | undefined | null): array is string[] {
	if (!array) {
		return false;
	}
	return typeof array[0] === 'string';
}

/**
 * Type Guard ensuring the array is numbers
 */
function isNumberArray(array: string[] | number[] | boolean[] | undefined | null): array is number[] {
	if (!array) {
		return false;
	}
	return typeof array[0] === 'number';
}
